import { useEffect, useRef, useState } from 'react'
import { Input, Label } from '@/shared/ui/primitives'
import {
  useMapboxAutocomplete,
  type AddressSelection,
} from './use-mapbox-autocomplete'

export type AddressInputProps = {
  id?: string
  label?: string
  value: string
  onChange: (value: string) => void
  onSelect: (selection: AddressSelection) => void
  placeholder?: string
  disabled?: boolean
}

const LISTBOX_ID = 'address-input-listbox'

export function AddressInput({
  id = 'address-input',
  label = 'Address line 1',
  value,
  onChange,
  onSelect,
  placeholder,
  disabled,
}: AddressInputProps) {
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)

  const {
    suggestions,
    isSearching,
    searchPlaces,
    selectSuggestion,
    clearSuggestions,
  } = useMapboxAutocomplete()

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    onChange(next)
    searchPlaces(next)
    setOpen(next.length >= 3)
    setHighlightedIndex(-1)
  }

  const handleSelect = async (mapboxId: string) => {
    const result = await selectSuggestion(mapboxId)
    if (result) {
      onSelect(result)
    }
    setOpen(false)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (suggestions.length === 0) return
      setHighlightedIndex((index) =>
        index >= suggestions.length - 1 ? 0 : index + 1,
      )
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (suggestions.length === 0) return
      setHighlightedIndex((index) =>
        index <= 0 ? suggestions.length - 1 : index - 1,
      )
      return
    }
    if (event.key === 'Enter') {
      if (open && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        event.preventDefault()
        event.stopPropagation()
        void handleSelect(suggestions[highlightedIndex].mapboxId)
      }
      return
    }
    if (event.key === 'Escape') {
      setOpen(false)
      clearSuggestions()
      setHighlightedIndex(-1)
    }
  }

  const clearSuggestionsRef = useRef(clearSuggestions)
  clearSuggestionsRef.current = clearSuggestions

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        clearSuggestionsRef.current()
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  const showListbox = open && suggestions.length > 0

  return (
    <div ref={containerRef} className="relative grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        role="combobox"
        aria-expanded={showListbox}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          highlightedIndex >= 0
            ? `address-option-${highlightedIndex}`
            : undefined
        }
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {showListbox ? (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-md"
        >
          {suggestions.map((suggestion, index) => {
            const isHighlighted = highlightedIndex === index
            return (
              <li
                key={suggestion.mapboxId}
                id={`address-option-${index}`}
                role="option"
                aria-selected={isHighlighted}
                onMouseDown={(event) => {
                  event.preventDefault()
                  void handleSelect(suggestion.mapboxId)
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  isHighlighted ? 'bg-accent text-accent-foreground' : ''
                }`}
              >
                <div className="font-medium">{suggestion.name}</div>
                {suggestion.placeFormatted ? (
                  <div className="text-xs text-muted-foreground">
                    {suggestion.placeFormatted}
                  </div>
                ) : null}
              </li>
            )
          })}
          {isSearching ? (
            <li
              aria-hidden="true"
              className="px-3 py-2 text-xs text-muted-foreground"
            >
              Searching…
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
